import {
  type ComputedRef,
  computed,
  type MaybeRefOrGetter,
  nextTick,
  type Ref,
  ref,
  type ShallowRef,
  shallowRef,
  toValue,
} from 'vue';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { type Overlay, useUiStore } from '@/app/stores/ui';
import { LEGACY_GALLERY_TRANSPARENT_PIXEL } from '@/components/legacy-gallery/legacyGalleryTypes';
import { kioskRequiresPrivacyConsent } from '@/components/steps/gdpr-consent/privacyConsent';
import { analytics } from '@/services/Analytics';
import { kioskStyle } from '@/services/KioskStyle';
import { session } from '@/services/Session';
import { sentryService } from '@/services/telemetry/sentry';
import { track, warnMessageOf } from '@/shared/utils/track-telemetry';
import type {
  ArtStyleClickEvent,
  ArtStyleGalleryItem,
  ArtStyleGridClass,
  ArtStyleSourceStyle,
} from './artStyleTypes';

type ArtStyleInitializedHandler = () => void;
type ArtStyleSourceValue = ArtStyleSourceStyle | null | undefined;

interface ArtStyleKioskConfig {
  privacy_policy_variant?: unknown;
  show_privacy_popup?: boolean | null;
}

interface ArtStyleStore {
  isVerticalKiosk: boolean;
  kiosk?: ArtStyleKioskConfig | null;
  styles: ArtStyleSourceValue[];
}

interface ArtStyleKioskStyle {
  EVENTS: { INITIALIZED: string };
  enableSelector: boolean | null | undefined;
  ever: (event: string, handler: ArtStyleInitializedHandler) => void;
  off: (event: string, handler: ArtStyleInitializedHandler) => void;
  selectStyle: (styleId: string | undefined) => void;
}

interface ArtStyleSession {
  gdprConsent: boolean | null | undefined;
}

interface ArtStyleRouter {
  redirectToStep: (
    stepId: string,
    options?: { query: { next: string } }
  ) => unknown;
}

interface ArtStyleAnalytics {
  METRICS: {
    style_id: string;
    time_style_selected: string;
  };
  trackData: (data: Record<string, unknown>) => unknown;
  trackTimeTo: (metric: string) => unknown;
}

export interface UseArtStyleFlowOptions {
  stylePreviewAspectRatio: MaybeRefOrGetter<number>;
}

export interface ArtStyleFlow {
  customGridClass: ComputedRef<ArtStyleGridClass>;
  enter: (exposePublicApi: () => void) => void;
  galleryItems: Ref<ArtStyleGalleryItem[]>;
  generateGalleryItems: (styles: unknown) => ArtStyleGalleryItem[];
  gridRows: ComputedRef<number | null>;
  hasSelectedStyle: ComputedRef<boolean>;
  initializedHandler: ShallowRef<ArtStyleInitializedHandler | null | undefined>;
  isUnmounted: ShallowRef<boolean | undefined>;
  leave: () => void;
  loadingIndicator: ShallowRef<Overlay | null | undefined>;
  navigateToPhotoStep: () => void;
  navigationTimerId: ShallowRef<
    ReturnType<typeof setTimeout> | null | undefined
  >;
  onGalleryItemClick: (
    event: ArtStyleClickEvent,
    item: ArtStyleGalleryItem
  ) => void;
  onKioskStyleInitialize: () => void;
  selectionLocked: ShallowRef<boolean>;
  shouldShowStyleSelector: () => boolean;
  ui: ReturnType<typeof useUiStore>;
}

export function useArtStyleFlow({
  stylePreviewAspectRatio,
}: UseArtStyleFlowOptions): ArtStyleFlow {
  const artStyleStore = store as unknown as ArtStyleStore;
  const styleService = kioskStyle as unknown as ArtStyleKioskStyle;
  const artStyleSession = session as unknown as ArtStyleSession;
  const artStyleRouter = router as unknown as ArtStyleRouter;
  const artStyleAnalytics = analytics as unknown as ArtStyleAnalytics;
  const ui = useUiStore();

  const galleryItems = ref<ArtStyleGalleryItem[]>([]);
  const selectionLocked = shallowRef(false);
  const isUnmounted = shallowRef<boolean>();
  const loadingIndicator = shallowRef<Overlay | null>();
  const initializedHandler = shallowRef<ArtStyleInitializedHandler | null>();
  const navigationTimerId = shallowRef<ReturnType<typeof setTimeout> | null>();

  const hasSelectedStyle = computed(() =>
    galleryItems.value.some((item) => item.selected)
  );

  const customGridClass = computed<ArtStyleGridClass>(() => {
    const count = galleryItems.value.length;
    if (count === 2) {
      return 'ArtStyle__custom-grid--two';
    }
    if (count === 3) {
      return 'ArtStyle__custom-grid--three';
    }
    if (count === 4) {
      return 'ArtStyle__custom-grid--four';
    }
    if (count % 2 === 1) {
      return 'ArtStyle__custom-grid--odd';
    }
    return 'ArtStyle__custom-grid--even';
  });

  const gridRows = computed(() => {
    const count = galleryItems.value.length;
    if (count <= 4) {
      return null;
    }
    return Math.ceil(count / 2);
  });

  function shouldShowStyleSelector(): boolean {
    return (
      styleService.enableSelector === true && artStyleStore.styles.length > 1
    );
  }

  function generateGalleryItems(styles: unknown): ArtStyleGalleryItem[] {
    if (!Array.isArray(styles) || styles.length === 0) {
      track(
        '[ArtStyle] Sentry captureMessage failed:',
        () => {
          sentryService.captureMessage(
            'generateGalleryItems called with invalid styles',
            {
              level: 'warning',
              tags: {
                component: 'ArtStyle',
                action: 'generate_gallery_items',
              },
              extra: {
                stylesType: typeof styles,
                stylesLength: (
                  styles as { length?: unknown } | null | undefined
                )?.length,
                isArray: Array.isArray(styles),
              },
            }
          );
        },
        warnMessageOf
      );
      return [];
    }

    const missingPreviewIds: Array<string | undefined> = [];
    const sourceStyles = styles as ArtStyleSourceValue[];
    const items = sourceStyles.map((style, index): ArtStyleGalleryItem => {
      if (!style?.id) {
        track(
          '[ArtStyle] Sentry breadcrumb failed:',
          () => {
            sentryService.addBreadcrumb(
              'debug',
              'Invalid style object encountered',
              {
                index,
                styleKeys: style ? Object.keys(style) : null,
              },
              'warning'
            );
          },
          warnMessageOf
        );
      }

      const sourceStyle = style as ArtStyleSourceStyle;
      const hasPreview = !!sourceStyle.preview_url;
      if (!hasPreview) {
        missingPreviewIds.push(sourceStyle.id);
      }

      return {
        uuid: sourceStyle.uuid,
        id: sourceStyle.id,
        title: sourceStyle.title,
        url: sourceStyle.preview_url || LEGACY_GALLERY_TRANSPARENT_PIXEL,
        aspectRatio: toValue(stylePreviewAspectRatio),
        hasPreview,
        selected: false,
      };
    });

    if (missingPreviewIds.length > 0) {
      track(
        '[ArtStyle] Sentry breadcrumb failed:',
        () => {
          sentryService.addBreadcrumb(
            'data',
            'Some styles missing preview images',
            {
              missingCount: missingPreviewIds.length,
              missingStyleIds: missingPreviewIds,
              totalStyles: sourceStyles.length,
            },
            'warning'
          );
        },
        warnMessageOf
      );
    }

    return items;
  }

  function navigateToPhotoStep(): void {
    const needsGdpr =
      kioskRequiresPrivacyConsent(artStyleStore.kiosk) &&
      artStyleSession.gdprConsent !== true;

    if (needsGdpr) {
      artStyleRouter.redirectToStep('gdpr-consent', {
        query: { next: 'photo-from-webcam' },
      });
      return;
    }
    artStyleRouter.redirectToStep('photo-from-webcam');
  }

  function onKioskStyleInitialize(): void {
    const shouldShowSelector = shouldShowStyleSelector();
    const stylesCount = artStyleStore.styles?.length || 0;

    track(
      '[ArtStyle] Sentry breadcrumb failed:',
      () => {
        sentryService.addBreadcrumb('ui.action', 'ArtStyle initialized', {
          shouldShowSelector,
          stylesCount,
          enableSelector: styleService.enableSelector,
        });
      },
      warnMessageOf
    );

    track(
      '[Sentry] Context failed:',
      () => {
        sentryService.setContext('art_styles', {
          available_count: stylesCount,
          selector_enabled: styleService.enableSelector,
          show_selector: shouldShowSelector,
          style_ids:
            artStyleStore.styles
              ?.slice(0, 5)
              .map((style) => (style as ArtStyleSourceStyle).id) || [],
        });
      },
      warnMessageOf
    );

    if (shouldShowSelector) {
      galleryItems.value = generateGalleryItems(artStyleStore.styles);

      track(
        '[ArtStyle] Sentry breadcrumb failed:',
        () => {
          sentryService.addBreadcrumb('ui.render', 'Style gallery rendered', {
            itemCount: galleryItems.value.length,
            itemsWithPreviews: galleryItems.value.filter(
              (item) => item.hasPreview
            ).length,
          });
        },
        warnMessageOf
      );
      return;
    }

    const autoSelectedStyleId = artStyleStore.styles[0]?.id;
    if (autoSelectedStyleId) {
      track(
        '[ArtStyle] Sentry breadcrumb failed:',
        () => {
          sentryService.addBreadcrumb(
            'ui.action',
            'Auto-selecting single style',
            {
              styleId: autoSelectedStyleId,
              reason: 'selector_disabled_or_single_style',
            }
          );
        },
        warnMessageOf
      );
      styleService.selectStyle(autoSelectedStyleId);
      navigateToPhotoStep();
      return;
    }

    track(
      '[ArtStyle] Sentry captureMessage failed:',
      () => {
        sentryService.captureMessage(
          'Art style missing - no styles available',
          {
            level: 'error',
            tags: { component: 'ArtStyle', action: 'style_missing' },
            extra: {
              stylesArray: artStyleStore.styles,
              enableSelector: styleService.enableSelector,
            },
          }
        );
      },
      warnMessageOf
    );
    ui.showError('art_style_missing');
  }

  function onGalleryItemClick(
    _event: ArtStyleClickEvent,
    item: ArtStyleGalleryItem
  ): void {
    if (selectionLocked.value) {
      return;
    }
    if (hasSelectedStyle.value) {
      return;
    }

    selectionLocked.value = true;
    const { uuid, id, title } = item;
    const index = galleryItems.value.findIndex(
      (galleryItem) => galleryItem.uuid === uuid
    );

    if (index === -1) {
      selectionLocked.value = false;
      track(
        '[ArtStyle] Sentry captureMessage failed:',
        () => {
          sentryService.captureMessage(
            'Style selection failed - item not found in gallery',
            {
              level: 'warning',
              tags: {
                component: 'ArtStyle',
                action: 'style_selection_error',
              },
              extra: {
                uuid,
                id,
                galleryItemCount: galleryItems.value.length,
              },
            }
          );
        },
        warnMessageOf
      );
      return;
    }

    const selectedItem = galleryItems.value[index] as ArtStyleGalleryItem;
    selectedItem.selected = true;

    track(
      '[ArtStyle] Sentry breadcrumb failed:',
      () => {
        sentryService.addBreadcrumb('user.interaction', 'Art style selected', {
          styleId: id,
          styleTitle: title,
          styleIndex: index,
          totalStyles: galleryItems.value.length,
        });
      },
      warnMessageOf
    );

    track(
      '[Sentry] Context/tag failed:',
      () => {
        sentryService.setContext('selected_style', {
          id,
          title,
          uuid,
          selection_index: index,
          timestamp: new Date().toISOString(),
        });
        sentryService.setTag('selected_style_id', id);
      },
      warnMessageOf
    );

    artStyleAnalytics.trackTimeTo(
      artStyleAnalytics.METRICS.time_style_selected
    );
    artStyleAnalytics.trackData({
      [artStyleAnalytics.METRICS.style_id]: id,
    });

    navigationTimerId.value = setTimeout(() => {
      navigationTimerId.value = null;
      if (isUnmounted.value) {
        return;
      }
      styleService.selectStyle(id);
      navigateToPhotoStep();
    }, 1000);
  }

  function enter(exposePublicApi: () => void): void {
    const mountStart = performance.now();
    isUnmounted.value = false;
    exposePublicApi();

    track(
      '[Sentry] Breadcrumb failed:',
      () => {
        sentryService.addBreadcrumb(
          'navigation',
          'ArtStyle component mounted',
          {
            component: 'ArtStyle',
            isVerticalKiosk: artStyleStore.isVerticalKiosk,
          }
        );
      },
      warnMessageOf
    );

    loadingIndicator.value = ui.createLoadingIndicator();
    const handler = (): void => {
      if (isUnmounted.value) {
        loadingIndicator.value?.hide();
        loadingIndicator.value = null;
        return;
      }

      try {
        onKioskStyleInitialize();
      } catch (error) {
        sentryService.captureException(error, {
          tags: { component: 'ArtStyle', action: 'style_initialization' },
          extra: {
            stylesCount: artStyleStore.styles?.length || 0,
            enableSelector: styleService.enableSelector,
          },
        });
        ui.showError('art_style_initialization_failed');
      } finally {
        loadingIndicator.value?.hide();
        loadingIndicator.value = null;
      }
    };
    initializedHandler.value = handler;

    styleService.ever(styleService.EVENTS.INITIALIZED, handler);

    void nextTick(() => {
      const mountDuration = performance.now() - mountStart;
      try {
        sentryService.trackMetric(
          'component.mount_time_ms',
          mountDuration,
          'millisecond',
          {
            component: 'ArtStyle',
            style_count: artStyleStore.styles?.length || 0,
          }
        );
      } catch {
        return;
      }
    });
  }

  function leave(): void {
    isUnmounted.value = true;

    if (navigationTimerId.value != null) {
      clearTimeout(navigationTimerId.value);
      navigationTimerId.value = null;
    }

    if (initializedHandler.value) {
      styleService.off(
        styleService.EVENTS.INITIALIZED,
        initializedHandler.value
      );
      initializedHandler.value = null;
    }

    if (loadingIndicator.value) {
      loadingIndicator.value.hide();
      loadingIndicator.value = null;
    }

    track(
      '[ArtStyle] Failed to clear Sentry context:',
      () => {
        sentryService.setContext('art_styles', null);
        sentryService.setContext('selected_style', null);
      },
      warnMessageOf
    );
  }

  return {
    customGridClass,
    enter,
    galleryItems,
    generateGalleryItems,
    gridRows,
    hasSelectedStyle,
    initializedHandler,
    isUnmounted,
    leave,
    loadingIndicator,
    navigateToPhotoStep,
    navigationTimerId,
    onGalleryItemClick,
    onKioskStyleInitialize,
    selectionLocked,
    shouldShowStyleSelector,
    ui,
  };
}
